import { useState, useEffect } from 'react';
import { fetchSiteSettings } from '../lib/api';

interface SiteSettings {
  adminEmail: string;
  analyticsTrackingId: string;
  autoApprovalCriteria: string;
  companyName: string;
  contactSubtitle: string;
  contactTitle: string;
  coordinateDisplayFormat: string;
  defaultCountry: string;
  defaultSubmissionStatus: string;
  distanceCalculationMethod: string;
  facebookUrl: string;
  footerCopyright: string;
  homepageHeroSubtitle: string;
  homepageHeroTitle: string;
  instagramUrl: string;
  maxFileUploadSize: number;
  metaDescription: string;
  openGraphImage: string;
  requiredFields: string;
  siteName: string;
  submitViewSubtitle: string;
  submitViewTitle: string;
  supportedImageFormats: string;
  supportEmail: string;
  twitterUrl: string;
}

export const useSettings = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedSettings = await fetchSiteSettings();
      setSettings(fetchedSettings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Helper function to refresh settings (useful after admin updates)
  const refreshSettings = () => {
    loadSettings();
  };

  // Helper functions for common settings
  const getMaxFileSize = () => settings?.maxFileUploadSize || 2097152; // 2MB default
  const getSupportedFormats = () => settings?.supportedImageFormats?.split(', ') || ['JPEG', 'PNG', 'GIF', 'WebP'];
  const getDefaultCountry = () => settings?.defaultCountry || 'USA';
  const getDistanceUnit = () => settings?.distanceCalculationMethod === 'Miles' ? 'mi' : 'km';

  return {
    settings,
    loading,
    error,
    refreshSettings,
    // Helper functions
    getMaxFileSize,
    getSupportedFormats,
    getDefaultCountry,
    getDistanceUnit,
  };
}; 